/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable no-console */
import React, {
 useCallback,
 useEffect,
 useMemo,
 useState,
} from 'react'
import {PageTitle} from 'layout/master-layout'
import Spinner from 'layout/common/Spinner'
import {useAuth} from 'context/AuthProvider'
import {
 IProductCatalogue,
 IProductVariant,
 IVariantConfirmModal,
} from 'models/ProductManagement'
import {
 fetchSalesCatalogueList,
 productVariantUpdate,
 distributorCatalogueUpdate,
} from 'utils/api/productManagementService'
import {useTranslation} from 'react-i18next'
import {useNavigate} from 'react-router-dom'
import {ColumnProps, IFormField} from 'models/Common'
import {
 formatDateForDisplay,
 formatNumberForDisplay,
} from 'utils/helper'
import {KTFilterBox} from 'helpers/ui-components/KTFilterBox'
import {fetchFilterConfig} from 'utils/api/auth/commonService'

import {toast} from 'react-toastify'
import KTTable from 'helpers/ui-components/KTTable'
import {KTSearchBox} from 'helpers/ui-components/KTSearchBox'
import {createFilterPayload} from 'utils/helpers/genericFilterForm'
import {KTConfirmPopup} from 'helpers/ui-components/KTConfirmPopup'
import VariantRows from './VariantRows'
import {
 getProductActiveSkuCounts,
 getVariantActiveStatus,
} from './productCatalogueUtils'
import {
 getProductCataloguePath,
 getProductDetailsPath,
 getProductListPath,
} from './productCatalogueRoutes'
import {USER_CATEGORY} from 'constants/userCategory'

const formatMrpRange = (range: string) => {
 const parts = range.split('-')
 if (parts.length === 2) {
  return `${formatNumberForDisplay(
   parseFloat(parts[0])
  )} - ${formatNumberForDisplay(parseFloat(parts[1]))}`
 }
 return range
}

const getLatestVariantUpdateDate = (
 variants: IProductVariant[]
) => {
 const dates = variants
  .map((v) => v.last_updated_variant_price)
  .filter(Boolean)
 if (!dates.length) return ''
 return dates.sort().reverse()[0]
}

const ProductCataloguePage = () => {
 const {t} = useTranslation()
 const navigate = useNavigate()
 const {userData} = useAuth()
 const productCatalogueAccessDetails =
  userData?.userSubModule?.productCatalogue?.homePage
 const productListAccessDetails =
  userData?.userSubModule?.productList?.homePage
 const isDistributorAdmin =
  userData?.userCategoryKey ===
  USER_CATEGORY.DISTRIBUTOR_DIRECTOR
 const [loader, setLoader] = useState(false)
 const [confirmModalData, setConfirmModalData] =
  useState<IVariantConfirmModal | null>(null)
 const [searchTerm, setSearchTerm] = useState('')
 const [filterCount, setFilterCount] = useState(0)
 const [currentPage, setCurrentPage] = useState(1)
 const [itemsPerPage, setItemsPerPage] = useState(10)
 const [totalRecords, setTotalRecords] = useState(0)
 const [selectedStatus, setSelectedStatus] = useState<
  'ALL' | 'ACTIVE' | 'INACTIVE'
 >('ALL')
 const [filterFormData, setFilterFormData] =
  useState<IFormField | null>(null)
 const [filterConfig, setFilterConfig] = useState<
  IFormField[]
 >([])
 const [salesCatalogueList, setSalesCatalogueList] =
  useState<IProductCatalogue[]>([])
 const [expandedProductIds, setExpandedProductIds] =
  useState<Set<number>>(new Set())
 const [editingProductId, setEditingProductId] = useState<
  number | null
 >(null)
 const [editedPrices, setEditedPrices] = useState<
  Record<number, string>
 >({})
 const [originalPrices, setOriginalPrices] = useState<
  Record<number, string>
 >({})
 const [pendingEditProduct, setPendingEditProduct] =
  useState<IProductCatalogue | null>(null)

 const resetExpandAndEditState = () => {
  setExpandedProductIds(new Set())
  setEditingProductId(null)
  setEditedPrices({})
  setOriginalPrices({})
 }

 const hasUnsavedPriceChanges = useCallback(() => {
  if (editingProductId == null) return false
  return Object.keys(editedPrices).some(
   (id) =>
    editedPrices[Number(id)] !== originalPrices[Number(id)]
  )
 }, [editingProductId, editedPrices, originalPrices])

 const getFilterConfig = async () => {
  setLoader(true)
  const res = await fetchFilterConfig({
   filter_type: 'PRODUCT',
   organisation_type: 'PMU',
  })
  if (res) {
   setFilterConfig(res?.applicableFilters ?? [])
  }
  setLoader(false)
 }

 const getSalesCatalogueList = async (
  pageNo: number,
  options?: {preserveExpand?: boolean}
 ) => {
  setLoader(true)
  setSalesCatalogueList([])
  setTotalRecords(0)
  if (options?.preserveExpand) {
   setEditingProductId(null)
   setEditedPrices({})
   setOriginalPrices({})
  } else {
   resetExpandAndEditState()
  }
  const payload = await createFilterPayload(
   filterConfig,
   filterFormData
  )
  setFilterCount(payload.totalAppliedFilters)
  delete payload.totalAppliedFilters
  const res = await fetchSalesCatalogueList({
   page: pageNo,
   page_size: itemsPerPage,
   ...payload,
   ...(searchTerm && {
    search: searchTerm,
   }),
   ...(selectedStatus !== 'ALL' && {
    is_active: selectedStatus === 'ACTIVE' ? true : false,
   }),
  })
  if (res) {
   setSalesCatalogueList(res.results ?? [])
   setTotalRecords(res?.count ?? 0)
  }
  setLoader(false)
 }

 const allExpanded =
  salesCatalogueList.length > 0 &&
  salesCatalogueList.every((product) =>
   expandedProductIds.has(product.product_id)
  )

 const toggleExpandAll = useCallback(() => {
  if (allExpanded) {
   setExpandedProductIds(new Set())
  } else {
   setExpandedProductIds(
    new Set(
     salesCatalogueList.map((product) => product.product_id)
    )
   )
  }
 }, [allExpanded, salesCatalogueList])

 const toggleExpandOne = (productId: number) => {
  setExpandedProductIds((prev) => {
   const next = new Set(prev)
   if (next.has(productId)) {
    next.delete(productId)
   } else {
    next.add(productId)
   }
   return next
  })
 }

 const handleToggleBtnClick = async (
  isConfirm: boolean
 ) => {
  if (isConfirm) {
   const variant: IVariantConfirmModal | null =
    confirmModalData
   if (variant) {
    setLoader(true)
    let res: string | null = null
    if (isDistributorAdmin) {
     res = await distributorCatalogueUpdate({
      id: variant.product_variant_id,
      is_active: !getVariantActiveStatus(
       variant,
       isDistributorAdmin
      ),
     })
    } else {
     res = await productVariantUpdate({
      id: variant.product_variant_id,
      is_active: !getVariantActiveStatus(
       variant,
       isDistributorAdmin
      ),
     })
    }
    setLoader(false)
    if (res) {
     toast.success(res)
    }
    await getSalesCatalogueList(currentPage, {
     preserveExpand: true,
    })
    setConfirmModalData(null)
   }
  } else {
   setConfirmModalData(null)
  }
 }

 const handleVariantToggle = (
  variant: IProductVariant,
  productName: string
 ) => {
  setConfirmModalData({
   ...variant,
   product_name: productName,
  })
 }

 const startEditingProduct = useCallback(
  (product: IProductCatalogue) => {
   setEditingProductId(product.product_id)
   setExpandedProductIds((prev) => {
    const next = new Set(prev)
    next.add(product.product_id)
    return next
   })
   const prices: Record<number, string> = {}
   product.variants.forEach((variant) => {
    const price = isDistributorAdmin
     ? variant.seller_price
     : variant.variant_price

    prices[variant.product_variant_id] = String(price ?? '')
   })
   setEditedPrices(prices)
   setOriginalPrices({...prices})
  },
  [isDistributorAdmin]
 )

 const handleProductEditClick = useCallback(
  (product: IProductCatalogue) => {
   if (
    editingProductId != null &&
    product.product_id !== editingProductId &&
    hasUnsavedPriceChanges()
   ) {
    setPendingEditProduct(product)
    return
   }
   startEditingProduct(product)
  },
  [
   editingProductId,
   hasUnsavedPriceChanges,
   startEditingProduct,
  ]
 )

 const handleDiscardConfirmClick = (isConfirm: boolean) => {
  if (isConfirm && pendingEditProduct) {
   startEditingProduct(pendingEditProduct)
  }
  setPendingEditProduct(null)
 }

 const handleCancelEdit = useCallback(() => {
  setEditingProductId(null)
  setEditedPrices({})
  setOriginalPrices({})
 }, [])

 const setEditedPrice = (
  variantId: number,
  value: string
 ) => {
  setEditedPrices((prev) => ({
   ...prev,
   [variantId]: value,
  }))
 }

 const handleSaveProductPrices = useCallback(
  async (product: IProductCatalogue) => {
   const priceLabel = isDistributorAdmin
    ? t('productMgmt.productListCol.salePrice')
    : t('productMgmt.productListCol.variantPrice')

   for (const variant of product.variants) {
    const price =
     editedPrices[variant.product_variant_id] ?? ''
   }

   setLoader(true)
   let hasError = false

   for (const variant of product.variants) {
    const price =
     editedPrices[variant.product_variant_id] ?? ''
    const original =
     originalPrices[variant.product_variant_id] ?? ''
    if (price === original) continue

    let res: string | null = null
    if (isDistributorAdmin) {
     res = await distributorCatalogueUpdate({
      id: variant.product_variant_id,

      seller_price: Number(price),
     })
    } else {
     res = await productVariantUpdate({
      id: variant.product_variant_id,
      variant_price: Number(price),
     })
    }
    if (!res) hasError = true
   }

   setLoader(false)
   if (!hasError) {
    toast.success(
     t('productMgmt.productListCol.saveSuccess')
    )
    handleCancelEdit()
    await getSalesCatalogueList(currentPage)
   }
  },
  [
   editedPrices,
   originalPrices,
   isDistributorAdmin,
   t,
   currentPage,
   handleCancelEdit,
  ]
 )

 const canEditActive =
  productListAccessDetails?.section?.activateSkuBtn
   ?.canEdit ?? false
 const canViewEdit =
  productListAccessDetails?.section?.editSkuBtn?.canView ??
  false
 const canEditPrice =
  productListAccessDetails?.section?.editSkuBtn?.canEdit ??
  false

 const tableColList = useMemo<
  Array<ColumnProps<IProductCatalogue>>
 >(
  () => [
   {
    key: 'product_id',
    title: t('productMgmt.productListCol.productId'),
    className: 'w-[120px]',
    render: (_, record) => {
     return (
      <div
       className={`${
        productCatalogueAccessDetails?.section
         ?.productDetails?.canView &&
        'cursor-pointer text-[#4E83E4] underline'
       } w-full `}
       onClick={() => {
        if (
         productCatalogueAccessDetails?.section
          ?.productDetails?.canView
        ) {
         navigate(
          getProductDetailsPath(record.product_id),
          {
           state: {returnPath: getProductListPath()},
          }
         )
        }
       }}
      >
       {record.product_id}
      </div>
     )
    },
   },
   {
    key: 'product_name',
    title: (
     <div className='flex items-center gap-2'>
      <span
       className='flex items-center justify-center w-6 h-6 rounded-lg border border-[rgba(255,255,255,0.22)] cursor-pointer bg-[rgba(255,255,255,0.16)]'
       onClick={() => toggleExpandAll()}
      >
       <i
        className={`fa ${
         allExpanded
          ? 'fa-chevron-down'
          : 'fa-chevron-right'
        } text-xs`}
        aria-hidden='true'
       ></i>
      </span>
      <span>
       {t('productMgmt.productListCol.productName')}
      </span>
     </div>
    ),
    className: 'w-[28%]',
    render: (_, record) => {
     const isExpanded = expandedProductIds.has(
      record.product_id
     )
     return (
      <div className='flex items-start gap-2'>
       <span
        className='cursor-pointer text-[#4E83E4] mt-1'
        onClick={() => toggleExpandOne(record.product_id)}
       >
        <i
         className={`fa ${
          isExpanded
           ? 'fa-chevron-down'
           : 'fa-chevron-right'
         } text-xs`}
         aria-hidden='true'
        ></i>
       </span>
       <div>
        <p className=' text-[#33393D]'>
         {record.product_name}
        </p>
        <p className='text-xs text-[#5A6066]'>
         {record.sku_count}{' '}
         {t('productMgmt.productListCol.skus')}
        </p>
       </div>
      </div>
     )
    },
   },
   {
    key: 'active_skus',
    title: t('productMgmt.productListCol.active'),
    className: 'w-[15%]',
    render: (_, record) => {
     const {activeSkuCount, totalSkuCount} =
      getProductActiveSkuCounts(record, isDistributorAdmin)
     return (
      <span className='text-sm text-[#33393D]'>
       {activeSkuCount}/{totalSkuCount}{' '}
       {t('productMgmt.productListCol.skusActive')}
      </span>
     )
    },
   },
   {
    key: 'mrp_price_range',
    title: t('productMgmt.productListCol.mrpPrice'),
    className: 'w-[20%]',
    render: (_, record) => {
     const lastUpdated = getLatestVariantUpdateDate(
      record.variants
     )
     return (
      <div className='w-full text-[#33393D] font-normal text-sm'>
       <div>
        <i className='fa fa-inr pr-0.5'></i>
        {formatMrpRange(record.mrp_price_range)}
       </div>
       {lastUpdated && (
        <div className='text-xs text-[#5A6066]'>
         Last updated on{' '}
         {formatDateForDisplay(lastUpdated, 'DD-MMM-YYYY')}
        </div>
       )}
      </div>
     )
    },
   },
   {
    key: 'sale_price',
    title: isDistributorAdmin
     ? t('productMgmt.productListCol.salePrice')
     : t('productMgmt.productListCol.variantPrice'),
    className: 'w-[15%]',
    render: () => null,
   },
   {
    key: 'action',
    title: '',
    className: 'w-[10%] text-center',
    render: (_, record) => {
     const isEditing =
      editingProductId === record.product_id

     if (!canViewEdit) return null

     return (
      <div className='flex items-center justify-end gap-2'>
       {isEditing ? (
        <>
         <button
          type='button'
          disabled={!canEditPrice}
          className='px-4 py-1.5 bg-[#4E83E4] text-white rounded-lg text-sm font-medium disabled:bg-[#C5CED6]'
          onClick={async () =>
           handleSaveProductPrices(record)
          }
         >
          {t('common.save')}
         </button>
         <button
          type='button'
          className='w-8 h-8 border border-[#E6EBF0] rounded-full flex items-center justify-center bg-white text-[#33393D]'
          onClick={handleCancelEdit}
         >
          <i
           className='fa fa-times text-sm'
           aria-hidden='true'
          ></i>
         </button>
        </>
       ) : (
        <button
         type='button'
         disabled={!canEditPrice}
         className='flex items-center gap-1.5 px-4 py-1.5 border border-[#4E83E4] rounded-lg text-[#4E83E4] text-sm font-medium bg-white disabled:border-[#C5CED6] disabled:text-[#C5CED6]'
         onClick={() => handleProductEditClick(record)}
        >
         <i
          className='fa fa-pencil text-xs'
          aria-hidden='true'
         ></i>
         {t('productMgmt.productListCol.edit')}
        </button>
       )}
      </div>
     )
    },
   },
  ],
  [
   t,
   allExpanded,
   expandedProductIds,
   editingProductId,
   productCatalogueAccessDetails,
   toggleExpandAll,
   isDistributorAdmin,
   canViewEdit,
   canEditPrice,
   handleSaveProductPrices,
   handleProductEditClick,
   handleCancelEdit,
  ]
 )

 const handlePageChange = async (pageNo: number) => {
  setCurrentPage(pageNo)
  await getSalesCatalogueList(pageNo)
 }

 useEffect(() => {
  void getFilterConfig()
 }, [])

 useEffect(() => {
  if (currentPage !== 1) {
   setCurrentPage(1)
  }
  void getSalesCatalogueList(1)
 }, [
  filterFormData,
  searchTerm,
  itemsPerPage,
  selectedStatus,
 ])

 return (
  <>
   {loader && <Spinner />}
   {confirmModalData && (
    <KTConfirmPopup
     showConfirmModel={confirmModalData ? true : false}
     handleConfirmBtnClick={handleToggleBtnClick}
     yesBtnText={t('common.yes')}
     noBtnText={t('common.no')}
     title={t('common.confirmModalTitle')}
     subTitle={`${t('common.confirmModalSubTitle')} ${
      getVariantActiveStatus(
       confirmModalData,
       isDistributorAdmin
      )
       ? 'Inactive'
       : 'Active'
     } this ${confirmModalData.product_sku_name}?`}
    />
   )}
   {pendingEditProduct && (
    <KTConfirmPopup
     showConfirmModel={!!pendingEditProduct}
     handleConfirmBtnClick={handleDiscardConfirmClick}
     yesBtnText={t('common.yes')}
     noBtnText={t('common.no')}
     title={t('common.confirmModalTitle')}
     subTitle={t('common.discardChangesConfirm')}
    />
   )}
   <div className='w-full min-h-full'>
    <div className='my-6 border-[#E6EBF0] border-b'>
     <p className='text-[#2F2F2F] text-xl font-medium'>
      {t('productMgmt.listOfProducts')}
     </p>
     <div className='flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4'>
      <div className='xl:flex-1 xl:max-w-[45%]'>
       <KTSearchBox
        label={t('productMgmt.search')}
        placeholder={t('productMgmt.searchText')}
        setSearchTerm={setSearchTerm}
       />
      </div>

      <div className='flex flex-wrap items-center gap-3 xl:justify-end'>
       <span className='text-sm text-[#33393D] whitespace-nowrap'>
        Quick Filters
       </span>

       {[
        {label: 'All', value: 'ALL'},
        {label: 'Active', value: 'ACTIVE'},
        {label: 'Inactive', value: 'INACTIVE'},
       ].map((item) => (
        <button
         key={item.value}
         type='button'
         onClick={() =>
          setSelectedStatus(
           item.value as 'ALL' | 'ACTIVE' | 'INACTIVE'
          )
         }
         className={`px-4 py-2 rounded-lg border text-sm whitespace-nowrap ${
          selectedStatus === item.value
           ? 'bg-[#4E83E4] text-white border-[#4E83E4]'
           : 'bg-white text-[#33393D] border-[#D5DDE5]'
         }`}
        >
         {item.label}
        </button>
       ))}
       <KTFilterBox
        parentId={'Product_list'}
        btnText={t('productMgmt.filters')}
        containerClass='bg-white border-none mt-4 pl-0'
        dateRangeContainerClass='mx-6'
        dateRangeTitle={t('common.dateRange')}
        showDateRangeBox={false}
        filterConfig={filterConfig}
        setLoader={setLoader}
        filterFormData={filterFormData}
        filterCount={filterCount}
        setFilterFormData={setFilterFormData}
       ></KTFilterBox>
      </div>
     </div>
    </div>

    <div className='w-full'>
     <KTTable
      data={salesCatalogueList}
      columns={tableColList}
      getRowId={(row) => row.product_id}
      expandedRowIds={expandedProductIds}
      renderExpandedRow={(product) => (
       <VariantRows
        product={product}
        isDistributorAdmin={isDistributorAdmin}
        isProductEditing={
         editingProductId === product.product_id
        }
        editedPrices={editedPrices}
        setEditedPrice={setEditedPrice}
        onToggleActive={handleVariantToggle}
        canEditActive={canEditActive}
       />
      )}
      headerClass='bg-[#80868C]'
      headerColClass='!border-0 text-left'
      colClass='text-[#33393D] !text-sm font-normal !border-0 !py-3'
      tableClass=''
      rowClass='even:bg-[#F5F8FA]'
      showPagination={true}
      currentPage={currentPage}
      itemsPerPage={itemsPerPage}
      totalRecord={totalRecords}
      setItemsPerPage={setItemsPerPage}
      onPageChange={handlePageChange}
     />
    </div>
   </div>
  </>
 )
}

export default function ProductCataloguePageWrapper() {
 const {t} = useTranslation()
 return (
  <div className='container relative'>
   <div className='px-[72px] pt-6 pb-10 bg-white'>
    <PageTitle
     breadcrumbContainerClass={
      'flex text-[#5A6066] text-sm font-normal items-center pb-6 border-[#E6EBF0] border-b'
     }
     breadcrumbs={[
      {
       title: t('breadCrumbs.home'),
       path: '/home-page',
       isActive: false,
      },
      {
       title: t('breadCrumbs.productCatalogue'),
       path: getProductCataloguePath(),
       isActive: false,
      },
      {
       title: t('breadCrumbs.manageCatalogue'),
       path: '',
       isActive: true,
      },
     ]}
     description='Welcome'
    >
     Manage Product Catalogue
    </PageTitle>
    <ProductCataloguePage />
   </div>
  </div>
 )
}
